import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import apiClient from "../../services/api";
import { BookOpen, Clock, Users, Filter, Search, Heart } from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { eCourseService } from "../../services/elearning/eCourseService";
import StarRating from "../../components/common/StarRating";
import Pagination from "../../components/common/Pagination";

interface Course {
  _id: string;
  title: string;
  description: string;
  shortDescription?: string;
  instructor: {
    _id: string;
    firstName: string;
    lastName: string;
    avatar?: string;
  };
  category: string;
  level: "Beginner" | "Intermediate" | "Advanced";
  duration: {
    estimatedHours: number;
    estimatedMinutes: number;
  };
  pricing: {
    type: "free" | "paid";
    amount: number;
    currency: string;
  };
  registration: {
    type: "self" | "manual" | "invite-only";
    requiresApproval: boolean;
    maxStudents?: number;
  };
  status: string;
  visibility: string;
  thumbnail?: string;
  language: string;
  createdAt: string;
  // Additional computed fields that might be added by backend
  totalStudents?: number;
  rating?: {
    average: number;
    count: number;
  };
  isEnrolled?: boolean;
  enrollmentStatus?: "enrolled" | "pending" | "not_enrolled";
}

const StudentEcourseCatalog: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("");
  const [sortBy, setSortBy] = useState("popular");
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 9,
    total: 0,
    pages: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCourseForPayment, setSelectedCourseForPayment] =
    useState<Course | null>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [customerFirstName, setCustomerFirstName] = useState("");
  const [customerLastName, setCustomerLastName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [paymentInitiated, setPaymentInitiated] = useState(false);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [paymentStatus, setPaymentStatus] = useState<string>("pending");
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(
    null,
  );
  const [redirectCountdown, setRedirectCountdown] = useState<number>(5);

  // Countdown timer for successful payment redirect
  useEffect(() => {
    if (paymentStatus === "completed") {
      setRedirectCountdown(5);
      const countdownInterval = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            window.location.reload();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [paymentStatus]);

  // Function to check payment status
  const checkPaymentStatus = async (paymentId: string) => {
    try {
      const response = await apiClient.get(
        `/fees/payments/${paymentId}/status`,
      );
      // apiClient interceptor already extracts .data, so response is { success, data: {...} }
      return (response as any).data; // The payment object
    } catch (error) {
      console.error("Error checking payment status:", error);
      console.error(
        "Error details:",
        (error as any).response?.data || (error as any).message,
      );
      return null;
    }
  };

  // Start polling for payment status
  const startPaymentPolling = (paymentId: string) => {
    let pollCount = 0;
    const maxPolls = 100; // 100 * 3 seconds = 5 minutes maximum

    const interval = setInterval(async () => {
      pollCount++;

      // Stop polling after 5 minutes to prevent infinite polling
      if (pollCount >= maxPolls) {
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
        setPaymentStatus("failed");
        toast.error(
          "Payment verification timeout. Please check your payment status.",
        );
        return;
      }

      const payment = await checkPaymentStatus(paymentId);
      if (payment) {
        // Check both status fields for completion
        const isCompleted =
          payment.status === "completed" ||
          payment.verificationStatus === "verified";

        const isFailed =
          payment.status === "failed" ||
          payment.verificationStatus === "failed";

        const isCancelled =
          payment.status === "cancelled" ||
          payment.verificationStatus === "cancelled";

        if (isCompleted) {
          setPaymentStatus("completed");
          toast.success(
            "Payment successful! You are now enrolled in the course.",
            {
              duration: 5000,
            },
          );
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
          refetch(); // Refresh courses to show enrollment
        } else if (isFailed) {
          setPaymentStatus("failed");
          toast.error("Payment failed. Please try again or contact support.", {
            duration: 5000,
          });
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
        } else if (isCancelled) {
          setPaymentStatus("cancelled");
          toast.error("Payment was cancelled. You can try again when ready.", {
            duration: 5000,
          });
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
        } else {
          console.log("⏳ Payment still processing:", payment.status);
          setPaymentStatus(payment.status);
        }
      } else {
        console.log("⚠️ No payment data received from status check");
      }
    }, 3000); // Check every 3 seconds

    setPollingInterval(interval);
  }; // Fetch public courses
  const {
    data: coursesData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      "public-courses",
      searchTerm,
      selectedCategory,
      selectedLevel,
      sortBy,
    ],
    queryFn: () =>
      eCourseService.getPublicCourses({
        search: searchTerm,
        category: selectedCategory,
        level: selectedLevel,
      }),
  });

  const courses = coursesData?.data?.courses || [];

  const categories = [
    "Programming",
    "Design",
    "Business",
    "Marketing",
    "Language",
    "Science",
    "Mathematics",
    "Arts",
    "Health",
    "Technology",
    "Other",
  ];

  const levels = ["Beginner", "Intermediate", "Advanced"];

  const handleCourseClick = (courseId: string) => {
    navigate(`/student/elearning/course/${courseId}`);
  };

  const handleEnroll = async (
    courseId: string,
    registrationType: string,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation(); // Prevent course click navigation

    const course = (courses as Course[]).find((c) => c._id === courseId);
    if (!course) return;

    try {
      if (course.pricing.type === "paid") {
        // Show payment modal for paid courses
        setSelectedCourseForPayment(course);
        setShowPaymentModal(true);
        return;
      }

      if (registrationType === "self") {
        // Direct enrollment for free courses
        const response = await eCourseService.enrollInCourse(courseId);
        if (response.success) {
          // Refetch courses to update enrollment status
          refetch();
          // Show success message or navigate to course
          console.log("Successfully enrolled in course!");
        }
      } else if (registrationType === "manual") {
        // Request enrollment
        const response = await eCourseService.requestEnrollment(courseId);
        if (response.success) {
          // Refetch courses to update enrollment status
          refetch();
          console.log("Enrollment request submitted!");
        }
      }
    } catch (error) {
      console.error("Enrollment error:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to submit enrollment request. Please try again.",
      );
    }
  };

  const handlePaymentSubmit = async () => {
    if (!selectedCourseForPayment || !phoneNumber.trim()) {
      toast.error("Please enter your phone number");
      return;
    }

    if (!customerFirstName.trim()) {
      toast.error("Please enter your first name");
      return;
    }

    if (!customerLastName.trim()) {
      toast.error("Please enter your last name");
      return;
    }

    if (!customerEmail.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      console.log(
        "🚀 Initiating payment for course:",
        selectedCourseForPayment._id,
      );
      console.log("📦 Payment data:", {
        phoneNumber,
        customerFirstName,
        customerLastName,
        customerEmail,
      });

      const response = await apiClient.post(
        `/elearning/courses/${selectedCourseForPayment._id}/enroll`,
        {
          phoneNumber,
          customerFirstName,
          customerLastName,
          customerEmail,
        },
      );

      console.log("✅ Payment API response:", response);
      console.log("📊 Response success:", (response as any).success);
      console.log("📄 Response data:", (response as any).data);

      // IMPORTANT: apiClient interceptor already extracts response.data
      // So 'response' here is { success: true, message: "...", data: {...} }
      if ((response as any).success) {
        console.log("💳 Setting payment states...");
        setPaymentInitiated(true);
        setPaymentData((response as any).data); // Access response.data directly, not response.data.data
        setPaymentStatus("pending");
        console.log("✅ Payment states set - modal should now show");

        // Show success toast for STK Push initiation
        toast.success(
          (response as any).message ||
            "Payment request sent! Check your phone for M-Pesa prompt.",
          {
            duration: 5000,
          },
        );

        // Start polling for payment status
        const paymentId = (response as any).data?.paymentId;
        if (paymentId) {
          console.log("🔄 Starting payment polling with paymentId:", paymentId);
          startPaymentPolling(paymentId);
        } else {
          console.log("⚠️ No paymentId in response data");
          console.log("⚠️ Response data structure:", (response as any).data);
        }
      }
    } catch (error) {
      console.error("Payment initiation error:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to initiate payment. Please try again.",
      );
    }
  };

  const closePaymentModal = () => {
    // If payment is completed, the countdown timer will handle the redirect
    if (paymentStatus === "completed") {
      return;
    }

    // If payment failed, reset payment state but keep modal open to retry
    if (paymentStatus === "failed") {
      setPaymentInitiated(false);
      setPaymentData(null);
      setPaymentStatus("pending");
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
      return;
    }

    // For cancelled or other states, close modal completely
    setShowPaymentModal(false);
    setSelectedCourseForPayment(null);
    setPhoneNumber("");
    setCustomerFirstName("");
    setCustomerLastName("");
    setCustomerEmail("");
    setPaymentInitiated(false);
    setPaymentData(null);
    setPaymentStatus("pending");

    // Clear polling interval
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }

    refetch(); // Refresh courses to update enrollment status
  };

  const filteredAndSortedCourses = courses
    .filter((course: Course) => {
      const matchesSearch = course.title
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesCategory =
        !selectedCategory || course.category === selectedCategory;
      const matchesLevel = !selectedLevel || course.level === selectedLevel;
      return matchesSearch && matchesCategory && matchesLevel;
    })
    .sort((a: Course, b: Course) => {
      switch (sortBy) {
        case "popular":
          return (b.totalStudents || 0) - (a.totalStudents || 0);
        case "rating":
          return (b.rating?.average || 0) - (a.rating?.average || 0);
        case "newest":
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        case "alphabetical":
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

  // Update pagination when filteredAndSortedCourses changes
  React.useEffect(() => {
    const total = filteredAndSortedCourses.length;
    const pages = Math.ceil(total / pagination.limit);
    setPagination((prev) => ({
      ...prev,
      total,
      pages,
      hasNext: prev.page < pages,
      hasPrev: prev.page > 1,
    }));
  }, [filteredAndSortedCourses.length, pagination.limit, pagination.page]);

  // Calculate paginated courses
  const paginatedCourses = filteredAndSortedCourses.slice(
    (pagination.page - 1) * pagination.limit,
    pagination.page * pagination.limit,
  );

  const getLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case "beginner":
        return "text-white bg-gradient-to-r from-green-500 to-emerald-500";
      case "intermediate":
        return "text-white bg-gradient-to-r from-yellow-500 to-orange-500";
      case "advanced":
        return "text-white bg-gradient-to-r from-red-500 to-pink-500";
      default:
        return "text-slate-700 bg-slate-200";
    }
  };

  const getEnrollmentButtonText = (course: Course) => {
    if (course.isEnrolled) return "Continue Learning";
    if (course.enrollmentStatus === "pending") return "Enrollment Pending";
    if (course.registration.type === "self") return "Enroll Now";
    if (course.registration.type === "manual") return "Request Enrollment";
    return "Enrollment Closed";
  };

  const getEnrollmentButtonColor = (course: Course) => {
    if (course.isEnrolled)
      return "bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600";
    if (course.enrollmentStatus === "pending")
      return "bg-gradient-to-r from-yellow-500 to-orange-500 cursor-not-allowed";
    if (course.registration.type === "invite-only")
      return "bg-slate-400 cursor-not-allowed";
    return "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600";
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header - Modern & Clean */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-10">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900 mb-3 leading-tight">
              Course Catalog
            </h1>
            <p className="text-slate-600 text-sm sm:text-lg">
              Discover and enroll in courses to advance your learning
            </p>
          </div>
          <button
            onClick={() => navigate("/student/elearning/dashboard")}
            className="bg-gradient-to-r from-slate-600 to-slate-700 text-white rounded-xl hover:from-slate-700 hover:to-slate-800 transition-all duration-200 font-semibold shadow-sm hover:shadow-md"
          >
            Back to Dashboard
          </button>
        </div>

        {/* Search and Filters - Modern Design */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-slate-50/50 hover:bg-white"
                />
              </div>
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="bg-slate-100 text-slate-700 px-6 py-3 rounded-xl hover:bg-slate-200 transition-all duration-200 flex items-center justify-center gap-2 font-medium"
            >
              <Filter className="w-5 h-5" />
              Filters
            </button>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-slate-200 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-slate-50/50 hover:bg-white font-medium text-slate-700"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Level
                </label>
                <select
                  value={selectedLevel}
                  onChange={(e) => setSelectedLevel(e.target.value)}
                  className="w-full p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-slate-50/50 hover:bg-white font-medium text-slate-700"
                >
                  <option value="">All Levels</option>
                  {levels.map((level) => (
                    <option key={level} value={level}>
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Sort By
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-slate-50/50 hover:bg-white font-medium text-slate-700"
                >
                  <option value="popular">Most Popular</option>
                  <option value="rating">Highest Rated</option>
                  <option value="newest">Newest</option>
                  <option value="alphabetical">A-Z</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="text-slate-600 mb-6 font-medium text-lg">
          {filteredAndSortedCourses.length} course
          {filteredAndSortedCourses.length !== 1 ? "s" : ""} found
        </div>

        {/* Course Grid - Modern Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedCourses.map((course: Course) => (
            <div
              key={course._id}
              className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 hover:shadow-xl transition-all duration-300 cursor-pointer group overflow-hidden"
              onClick={() => handleCourseClick(course._id)}
            >
              {/* Course Image */}
              <div className="relative h-52 bg-gradient-to-br from-blue-500 to-purple-600 overflow-hidden">
                {course.thumbnail ? (
                  <img
                    src={course.thumbnail}
                    alt={course.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <BookOpen className="w-16 h-16 text-white/90" />
                  </div>
                )}
                <div className="absolute top-4 left-4">
                  <span
                    className={`px-3 py-1.5 rounded-full text-xs font-bold shadow-lg backdrop-blur-sm ${getLevelColor(
                      course.level,
                    )}`}
                  >
                    {course.level}
                  </span>
                </div>
                <div className="absolute top-4 right-4">
                  <button className="p-2 bg-white/95 backdrop-blur-sm rounded-full hover:bg-white shadow-lg transition-all duration-200">
                    <Heart className="w-4 h-4 text-slate-600" />
                  </button>
                </div>
              </div>

              {/* Course Content */}
              <div className="p-6">
                <div className="flex items-center gap-2 text-sm mb-3">
                  <span className="bg-slate-100 px-3 py-1 rounded-lg text-xs font-semibold text-slate-700">
                    {course.category}
                  </span>
                  <div className="flex items-center gap-1">
                    <StarRating
                      rating={course.rating?.average || 0}
                      size="sm"
                      readonly={true}
                      showValue={false}
                    />
                    <span className="text-xs ml-1 font-medium text-slate-600">
                      ({course.rating?.count || 0})
                    </span>
                  </div>
                </div>

                <h3 className="font-bold text-slate-900 mb-3 line-clamp-2 leading-tight text-lg group-hover:text-blue-600 transition-colors">
                  {course.title}({course.level})
                </h3>

                <p className="text-slate-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                  {course.description}
                </p>

                {/* Instructor */}
                {course.instructor ? (
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center shadow-sm">
                      <span className="text-xs font-bold text-white">
                        {course.instructor.firstName?.[0] || "?"}
                      </span>
                    </div>
                    <span className="text-sm text-slate-600 font-medium">
                      {course.instructor.firstName} {course.instructor.lastName}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-sm text-slate-600 font-medium">
                      Instructor: Unknown
                    </span>
                  </div>
                )}

                {/* Course Stats */}
                <div className="flex items-center justify-between text-sm text-slate-600 mb-5">
                  <div className="flex items-center gap-1.5">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100">
                      <Clock className="w-4 h-4 text-slate-600" />
                    </div>
                    <span className="font-medium">
                      {course.duration.estimatedHours}h
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100">
                      <Users className="w-4 h-4 text-slate-600" />
                    </div>
                    <span className="font-medium">
                      {course.totalStudents || 0}
                    </span>
                  </div>
                  {course.pricing.type === "paid" &&
                    course.pricing.amount > 0 && (
                      <div className="font-bold text-green-600 text-base">
                        KES {course.pricing.amount}
                      </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      navigate(`/student/elearning/course/${course._id}`)
                    }
                    className="flex-1 bg-slate-100 text-slate-700 px-4 py-2.5 rounded-xl hover:bg-slate-200 transition-all duration-200 text-sm font-semibold"
                  >
                    View Details
                  </button>
                  {course.isEnrolled ? (
                    <button
                      onClick={() =>
                        navigate(`/student/elearning/course/${course._id}`)
                      }
                      className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2.5 rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-200 text-sm font-semibold shadow-sm hover:shadow-md"
                    >
                      Continue Learning
                    </button>
                  ) : (
                    <button
                      onClick={(e) =>
                        handleEnroll(course._id, course.registration.type, e)
                      }
                      disabled={
                        course.registration.type === "invite-only" ||
                        course.enrollmentStatus === "pending"
                      }
                      className={`flex-1 text-white px-4 py-2.5 rounded-xl transition-all duration-200 text-sm font-semibold shadow-sm hover:shadow-md ${getEnrollmentButtonColor(
                        course,
                      )}`}
                    >
                      {getEnrollmentButtonText(course)}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {filteredAndSortedCourses.length > 0 && (
          <div className="mt-8 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-6">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.pages}
              onPageChange={(page) => setPagination({ ...pagination, page })}
              pageSize={pagination.limit}
              onPageSizeChange={(size) => {
                setPagination({ ...pagination, limit: size, page: 1 });
              }}
              showPageSizeSelector={true}
              className="w-full"
            />
          </div>
        )}

        {/* Summary */}
        {filteredAndSortedCourses.length > 0 && (
          <div className="mt-4 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-4">
            <p className="text-sm text-slate-700 font-semibold">
              Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
              {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
              of {pagination.total} courses
            </p>
          </div>
        )}

        {/* Empty State - Modern Design */}
        {filteredAndSortedCourses.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="flex items-center justify-center w-24 h-24 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 mb-6 shadow-lg">
              <BookOpen className="w-12 h-12 text-slate-600" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-3">
              No courses found
            </h3>
            <p className="text-slate-600 mb-8 max-w-md text-lg">
              Try adjusting your search criteria or browse all available
              courses.
            </p>
            <button
              onClick={() => {
                setSearchTerm("");
                setSelectedCategory("");
                setSelectedLevel("");
              }}
              className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Payment Modal - Modern Design */}
      {showPaymentModal && selectedCourseForPayment && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Pay with Equity Bank
            </h3>
            <h4 className="text-red p-4 mb-6 rounded-lg bg-red-50 border border-red-200 text-center font-semibold">
              Payment unavailable at the moment. Kindly contact the secretaries for assistance.
            </h4>
            {paymentInitiated ? (
              <div className="text-center">
                {paymentStatus === "pending" && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
                    <h4 className="text-xl font-bold text-blue-800 mb-3">
                      M-Pesa Payment Request Sent
                    </h4>
                    <p className="text-blue-700 mb-4 text-base">
                      Check your phone for the M-Pesa payment prompt and enter
                      your PIN to complete the payment.
                    </p>
                    {paymentData && (
                      <div className="bg-white rounded-lg p-4 space-y-2 text-left border border-blue-200">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-slate-600">
                            Course:
                          </span>
                          <span className="text-sm font-bold text-slate-900">
                            {paymentData.courseTitle}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-slate-600">
                            Amount:
                          </span>
                          <span className="text-sm font-bold text-green-600">
                            KES {paymentData.amount}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-slate-600">
                            Charge:
                          </span>
                          <span className="text-sm font-bold text-slate-700">
                            KES {paymentData.charge}
                          </span>
                        </div>
                        <div className="flex justify-between items-center border-t pt-2">
                          <span className="text-sm font-semibold text-slate-600">
                            Total Debited:
                          </span>
                          <span className="text-base font-bold text-blue-700">
                            KES {paymentData.amountDebited}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-slate-600">
                            Phone:
                          </span>
                          <span className="text-sm font-mono text-slate-900">
                            {paymentData.phoneNumber}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-slate-600">
                            Reference:
                          </span>
                          <span className="text-xs font-mono text-slate-700">
                            {paymentData.orderReference}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="mt-4 flex items-center justify-center text-sm text-blue-600">
                      <svg
                        className="animate-pulse w-4 h-4 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Waiting for payment confirmation...
                    </div>
                  </div>
                )}

                {paymentStatus === "completed" && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4">
                    <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <svg
                        className="w-10 h-10 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <h4 className="text-2xl font-bold text-green-800 mb-3">
                      Payment Successful!
                    </h4>
                    <p className="text-green-700 mb-4 text-base">
                      Your M-Pesa payment has been confirmed and you have been
                      successfully enrolled in the course.
                    </p>
                    {paymentData && (
                      <div className="bg-white rounded-lg p-4 space-y-2 text-left border border-green-200">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-slate-600">
                            Course:
                          </span>
                          <span className="text-sm font-bold text-slate-900">
                            {paymentData.courseTitle}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-slate-600">
                            Amount Paid:
                          </span>
                          <span className="text-sm font-bold text-green-600">
                            KES {paymentData.amountDebited}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-slate-600">
                            Reference:
                          </span>
                          <span className="text-xs font-mono text-slate-700">
                            {paymentData.orderReference}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="mt-4 bg-white rounded-lg p-4 border border-green-200">
                      <div className="flex items-center justify-center text-green-700">
                        <Clock className="w-5 h-5 mr-2" />
                        <span className="text-sm font-semibold">
                          Redirecting to courses in {redirectCountdown} second
                          {redirectCountdown !== 1 ? "s" : ""}...
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {paymentStatus === "failed" && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-4">
                    <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <svg
                        className="w-10 h-10 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <h4 className="text-2xl font-bold text-red-800 mb-3">
                      Payment Failed
                    </h4>
                    <p className="text-red-700 mb-4 text-base">
                      The M-Pesa payment could not be completed. This could be
                      due to:
                    </p>
                    <ul className="text-left text-red-700 mb-4 space-y-1 text-sm">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Insufficient balance in your M-Pesa account</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Incorrect PIN entry</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Payment request expired or cancelled</span>
                      </li>
                    </ul>
                    <p className="text-red-600 text-sm font-semibold">
                      Please try again or contact support if the issue persists.
                    </p>
                  </div>
                )}

                {paymentStatus === "cancelled" && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-4">
                    <div className="w-16 h-16 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <svg
                        className="w-10 h-10 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <h4 className="text-2xl font-bold text-yellow-800 mb-3">
                      Payment Cancelled
                    </h4>
                    <p className="text-yellow-700 mb-4 text-base">
                      The M-Pesa payment request was cancelled. You can try
                      again when you're ready to enroll.
                    </p>
                  </div>
                )}

                <button
                  onClick={() => {
                    if (paymentStatus === "completed") {
                      // Immediately redirect when button is clicked
                      window.location.reload();
                    } else {
                      closePaymentModal();
                    }
                  }}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white py-3 px-4 rounded-xl hover:from-blue-600 hover:to-indigo-600 font-semibold shadow-sm hover:shadow-md transition-all duration-200"
                >
                  {paymentStatus === "completed"
                    ? "View My Courses Now"
                    : paymentStatus === "failed"
                      ? "Try Again"
                      : "Close"}
                </button>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h4 className="font-bold text-slate-900 mb-3 text-lg">
                    {selectedCourseForPayment?.title}
                  </h4>
                  <p className="text-3xl font-bold text-slate-900 mb-3">
                    {selectedCourseForPayment?.pricing.currency}{" "}
                    {selectedCourseForPayment?.pricing.amount}
                  </p>
                  <p className="text-sm text-slate-600">
                    Enter your details for Equity payment
                  </p>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={customerFirstName}
                      onChange={(e) => setCustomerFirstName(e.target.value)}
                      placeholder="John"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-slate-50/50 hover:bg-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={customerLastName}
                      onChange={(e) => setCustomerLastName(e.target.value)}
                      placeholder="Doe"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-slate-50/50 hover:bg-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="john.doe@example.com"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-slate-50/50 hover:bg-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="254XXXXXXXXX"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-slate-50/50 hover:bg-white"
                      required
                    />
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className="flex-1 bg-slate-100 text-slate-700 py-3 px-4 rounded-xl hover:bg-slate-200 font-semibold transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePaymentSubmit}
                    // disabled={
                    //   !phoneNumber.trim() ||
                    //   !customerFirstName.trim() ||
                    //   !customerLastName.trim() ||
                    //   !customerEmail.trim()
                    // }
                    disabled
                    className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500 text-white py-3 px-4 rounded-xl hover:from-blue-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-sm hover:shadow-md transition-all duration-200"
                  >
                    Pay & Enroll
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentEcourseCatalog;
